import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import {
  selectClientLens,
  selectClientLensAssignees,
  selectReviewerBackPeriod,
  selectReviewerTimeUnit,
  TimeWhen,
} from "redux/slices/clientLens";
import { selectMyUserId } from "redux/slices/session";
import TextSelect from "components/TextSelect";
import { selectReviewerTimeWhen, setReviewerTimeInterval } from "redux/slices/clientLens";
import CreateCase from "routes/lenses/lens/CreateCase";
import { selectActiveFirmIdN } from "redux/slices/client";
import { selectOutcomeEnabledFormSections } from "redux/slices/form.slice";
import { HeaderCard, HeaderCardTitle } from "components/HeaderCard";
import { SBRProgress } from "SBR/SBRProgress";
import { useLensStats } from "helpers/hooks/useLensStats/useLensStats";
import SBRCaseTable from "SBR/admin/CaseTable";
import { IconButton } from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";

export default function SBRLensReviewer({ lensId }: { lensId: number }) {
  const clientLens = useAppSelector(selectClientLens);
  const outcomeEnabledFormSections = useAppSelector(selectOutcomeEnabledFormSections);
  const timeframe = useAppSelector(selectReviewerTimeWhen);
  const myUserId = useAppSelector(selectMyUserId);
  const dispatch = useAppDispatch();
  const [createCase, setCreateCase] = useState<boolean>(false);
  const assignees = useAppSelector(selectClientLensAssignees);
  const activeFirmId = useAppSelector(selectActiveFirmIdN);
  const unit = useAppSelector(selectReviewerTimeUnit);
  const backPeriod = useAppSelector(selectReviewerBackPeriod);
  const lensStats = useLensStats(lensId);
  const myAssignee = assignees.find((a) => a.id === myUserId);
  const [showMetrics, setShowMetrics] = useState(true);

  useEffect(() => {
    if (myUserId !== undefined) {
      lensStats.getReviewerSBRStats({
        unit,
        backPeriod,
        assigneeId: myUserId,
        firmId: activeFirmId,
      });
    }
  }, [lensId, activeFirmId, myUserId, unit, backPeriod]);

  const updateWhen = (timeWhen: TimeWhen) => {
    dispatch(setReviewerTimeInterval({ timeWhen }));
  };

  const options = [
    {
      label: "Timeframe: This Week",
      value: TimeWhen.ThisWeek,
    },
    {
      label: "Timeframe: Last Week",
      value: TimeWhen.LastWeek,
    },
    {
      label: "Timeframe: This Month",
      value: TimeWhen.ThisMonth,
    },
    {
      label: "Timeframe: Last Month",
      value: TimeWhen.LastMonth,
    },
    {
      label: "Timeframe: This Year",
      value: TimeWhen.ThisYear,
    },
    {
      label: "Timeframe: Last Year",
      value: TimeWhen.LastYear,
    },
  ];

  return (
    <>
      <div className="flex flex-col gap-4 border-t border-gray-100 pt-8 relative h-full min-h-0">
        <div
          className="absolute top-0 right-0 flex gap-1 items-center cursor-pointer"
          onClick={() => setShowMetrics((prev) => !prev)}>
          <p className="text-xs text-gray-400">{showMetrics ? "Hide" : "Show"} Metrics</p>
          <IconButton size="small">{showMetrics ? <ExpandLess /> : <ExpandMore />}</IconButton>
        </div>
        {showMetrics && (
          <div className="flex gap-4">
            <HeaderCard
              contentClassName="h-full"
              className="basis-1/2"
              title={<HeaderCardTitle title="Review Progress" />}>
              <div className="p-4 pb-6 h-full">
                {lensStats.reviewerCaseProgressBySectionStatsTotal > 0 ? (
                  <SBRProgress
                    lensId={lensId}
                    formSections={outcomeEnabledFormSections}
                    sectionStats={lensStats.reviewerCaseProgressBySection}
                  />
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <p>No sections are currently assigned to you.</p>
                  </div>
                )}
              </div>
            </HeaderCard>
            <HeaderCard
              contentClassName="h-full"
              title={
                <HeaderCardTitle className="flex text-sm font-semibold">
                  <TextSelect
                    value={timeframe}
                    onChange={(event) => updateWhen(event.target.value)}
                    options={options}
                  />
                  <p className="text-sm font-semibold">in Pattern</p>
                </HeaderCardTitle>
              }>
              <div className="p-4 flex flex-col items-center justify-center h-full">
                {lensStats.reviewerCompletedSectionsAndCases > 0 ? (
                  <div className="flex flex-col items-center justify-center gap-4">
                    <div className="flex flex-col gap-1 justify-center items-center">
                      <p className="font-semibold">{lensStats.reviewerCompletedCaseStats?.completedSections ?? 0}</p>
                      <p className="text-sm">completed sections</p>
                    </div>
                    <div className="flex flex-col gap-1 justify-center items-center">
                      <p className="font-semibold">{lensStats.reviewerCompletedCaseStats?.completedCases ?? 0}</p>
                      <p className="text-sm">completed cases</p>
                    </div>
                  </div>
                ) : (
                  <p className="text-center">
                    You have not completed any reviews{" "}
                    <strong>{`${timeframe.substring(0, 4)} ${timeframe.substring(4)}`}</strong>. Complete a review or
                    select a different time period to see review statistics.
                  </p>
                )}
              </div>
            </HeaderCard>
          </div>
        )}
        <div className="flex flex-col h-full gap-4 border-t border-gray-100 py-4 min-h-[600px]">
          <HeaderCard
            className="h-full min-h-0"
            contentClassName="h-full min-h-0"
            title={<HeaderCardTitle title="Cases" />}>
            <div className="p-4 h-full min-h-0 flex flex-col">
              <SBRCaseTable
                key={clientLens.id}
                lensId={clientLens.id}
                bulkActionsEnabled={false}
                staticAssignees={myUserId !== undefined ? [myUserId] : []}
                reviewer
              />
            </div>
          </HeaderCard>
        </div>
      </div>
      {createCase && (
        <CreateCase onCancel={() => setCreateCase(false)} lens={clientLens} initialAssignee={myAssignee} />
      )}
    </>
  );
}
